/**
 * <s-brand-index> — Client-side filtering for the A–Z brand list.
 *
 * Without JavaScript the letter links are plain in-page anchors and every
 * brand stays visible. With it:
 *   [data-brand-letter]  — Letter links (href="#brands-X") filter the list to that letter;
 *                          the link with data-brand-letter="all" shows everything again.
 *   [data-brand-search]  — Search input; filters brands by name as the buyer types.
 *   [data-brand-group]   — One group per letter (data-brand-group="X").
 *   [data-brand-item]    — One row per brand (data-brand-name="lowercased name").
 *   [data-brand-empty]   — Message shown when nothing matches.
 *   [data-brand-status]  — Polite live region; receives data-status-template with
 *                          "{{ count }}" replaced by the visible brand count.
 */
class SBrandIndex extends HTMLElement {
  #ac;
  #letter = 'all';
  #query = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    for (const link of this.querySelectorAll('[data-brand-letter]')) {
      link.addEventListener('click', (event) => {
        if (link.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        this.#letter = link.dataset.brandLetter || 'all';
        this.#render();
      }, opts);
    }

    const search = this.querySelector('[data-brand-search]');
    search?.addEventListener('input', () => {
      this.#query = search.value.trim().toLowerCase();
      if (this.#query) this.#letter = 'all';
      this.#render();
    }, opts);

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #render() {
    let visible = 0;

    for (const group of this.querySelectorAll('[data-brand-group]')) {
      const letterMatch = this.#letter === 'all' || group.dataset.brandGroup === this.#letter;
      let groupVisible = 0;

      for (const item of group.querySelectorAll('[data-brand-item]')) {
        const name = item.dataset.brandName || '';
        const show = letterMatch && (!this.#query || name.includes(this.#query));
        item.hidden = !show;
        if (show) groupVisible += 1;
      }

      group.hidden = groupVisible === 0;
      visible += groupVisible;
    }

    for (const link of this.querySelectorAll('[data-brand-letter]')) {
      if (link.dataset.brandLetter === this.#letter) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }

    const empty = this.querySelector('[data-brand-empty]');
    if (empty) empty.hidden = visible > 0;

    const status = this.querySelector('[data-brand-status]');
    const template = status?.dataset.statusTemplate;
    if (status && template && (this.#query || this.#letter !== 'all')) {
      status.textContent = template.replace('{{ count }}', String(visible));
    }
  }
}

if (!customElements.get('s-brand-index')) {
  customElements.define('s-brand-index', SBrandIndex);
}
